import translate from '@translate';
import React, { FC } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import TextField from 'src/components/TextField';
import { ProviderType } from 'src/types/provider';
import Switch from 'src/components/Switch';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Select from 'src/components/Select';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import { Typography, Box } from '@mui/material';
import { getProviderStatusOptionStyle } from 'src/utils/providerStatusColorMap';
import colorOptionFce from 'src/utils/colorOptionFce';
import { ApplicationInstallType } from 'src/types/ApplicationInstall';
import DateTimePicker from '../../components/DateTimePicker';

export type OnChangeMultipleType = (updates: Partial<ProviderType>) => void;

// Dropdown options for Shown in MyS (Ano/Ne)
const SHOWN_IN_MYS_OPTIONS: ConstantsCodeType[] = [
    { label: translate('utils.boolCodes.yes'), value: 1, color: 'green' },
    { label: translate('utils.boolCodes.no'), value: 0, color: 'red' },
];

const PIPES_ACTIVE_OPTIONS: ConstantsCodeType[] = [
    { label: translate('providerDetail.pipesDownload.no'), value: 0, color: 'red' },
    { label: translate('providerDetail.pipesDownload.yes'), value: 1, color: 'green' },
];

const PIPES_IN_TEST_OPTIONS: ConstantsCodeType[] = [
    { label: translate('providerDetail.pipesUpload.no'), value: 0, color: 'red' },
    { label: translate('providerDetail.pipesUpload.yes'), value: 1, color: 'green' },
];

export interface ItPropsType {
    onChange: OnChangeType<EditKeysType>;
    onChangeMultiple: OnChangeMultipleType;
    editData: ProviderType;
    editErrors: FormErrorsType;
    disableEdit: boolean;
    providerSystemCodes: ConstantsCodeType[];
    providersCodes: ConstantsCodeType[];
    providerStatusCodes: ConstantsCodeType[];
    permissionOnboardingEdit: boolean;
    newProvider: boolean;
    applicationInstallData: ApplicationInstallType | null;
}

const It: FC<ItPropsType> = (props) => {
    return (
        <>
            <Grid>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.ESHOP_KEY}
                        error={props.editErrors.ESHOP_KEY}
                        onChange={(value, error) => props.onChange('ESHOP_KEY', value, error)}
                        name="ESHOP_KEY"
                        label={translate('providerDetail.eshopKey')}
                        fullWidth
                        disabled={props.disableEdit || !props.newProvider || !props.permissionOnboardingEdit}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.DCIX_PROVIDER_ID}
                        error={props.editErrors.DCIX_PROVIDER_ID}
                        onChange={(value, error) => props.onChange('DCIX_PROVIDER_ID', value, error)}
                        name="DCIX_PROVIDER_ID"
                        label={translate('providerDetail.dcixProviderId')}
                        fullWidth
                        disabled={props.disableEdit || !props.permissionOnboardingEdit}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Select
                        options={props.providersCodes}
                        value={props.editData.VIRTUAL_ESHOP_PARENT}
                        error={props.editErrors.VIRTUAL_ESHOP_PARENT}
                        onChange={(value, error) => props.onChange('VIRTUAL_ESHOP_PARENT', value, error)}
                        name="VIRTUAL_ESHOP_PARENT"
                        label={translate('providerDetail.virtualEshopParent')}
                        fullWidth
                        disabled={props.disableEdit || !props.permissionOnboardingEdit}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <TextField
                        value={props.editData.SHOPTET_STOCK_RATIO}
                        error={props.editErrors.SHOPTET_STOCK_RATIO}
                        onChange={(value, error) => props.onChange('SHOPTET_STOCK_RATIO', value || null, error)}
                        name="SHOPTET_STOCK_RATIO"
                        label={translate('providerDetail.shoptetStockRatio')}
                        fullWidth
                        disabled={props.disableEdit || !props.permissionOnboardingEdit}
                        type="numberLocale"
                    />
                </GridItem>
            </Grid>
            {/* Status section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {translate('providerDetail.statusSection')}
                </Typography>
                <Grid>
                    <GridItem cols={3}>
                        <Select
                            options={props.providerStatusCodes}
                            value={props.editData.STATUS}
                            error={props.editErrors.STATUS}
                            onChange={(value, error) => props.onChange('STATUS', value, error)}
                            name="STATUS"
                            label={translate('providerDetail.status')}
                            fullWidth
                            disabled={props.disableEdit || !props.permissionOnboardingEdit}
                            optionStyleFce={getProviderStatusOptionStyle}
                        />
                    </GridItem>
                    {props.applicationInstallData && (
                        <>
                            <GridItem cols={3}>
                                <Select
                                    options={PIPES_ACTIVE_OPTIONS}
                                    value={props.applicationInstallData.active ? 1 : 0}
                                    name="PIPES_ACTIVE"
                                    label={translate('providerDetail.pipesActive')}
                                    fullWidth
                                    disabled={true}
                                    optionStyleFce={colorOptionFce}
                                />
                            </GridItem>
                            <GridItem cols={3}>
                                <Select
                                    options={PIPES_IN_TEST_OPTIONS}
                                    value={props.applicationInstallData.inTest ? 0 : 1}
                                    name="PIPES_IN_TEST"
                                    label={translate('providerDetail.pipesInTest')}
                                    fullWidth
                                    disabled={true}
                                    optionStyleFce={colorOptionFce}
                                />
                            </GridItem>
                        </>
                    )}

                    <GridItem cols={3}>
                        <Select
                            options={SHOWN_IN_MYS_OPTIONS}
                            value={props.editData.ACTIVE}
                            error={props.editErrors.ACTIVE}
                            onChange={(value, error) => props.onChange('ACTIVE', value, error)}
                            name="ACTIVE"
                            label={translate('providerDetail.shownInMys')}
                            fullWidth
                            disabled={props.disableEdit || !props.permissionOnboardingEdit}
                            optionStyleFce={colorOptionFce}
                        />
                    </GridItem>
                    <GridItem cols={3}>
                        <DateTimePicker
                            value={props.editData.PLANNED_LAUNCH}
                            error={props.editErrors.PLANNED_LAUNCH}
                            onChange={(value, error) => props.onChange('PLANNED_LAUNCH', value, error)}
                            name="PLANNED_LAUNCH"
                            label={translate('providerDetail.plannedLaunch')}
                            fullWidth
                            disabled={props.disableEdit || !props.permissionOnboardingEdit}
                            type="date"
                        />
                    </GridItem>
                    <GridItem cols={3}>
                        <DateTimePicker
                            value={props.editData.ACTUAL_LAUNCH}
                            error={props.editErrors.ACTUAL_LAUNCH}
                            onChange={(value, error) => props.onChange('ACTUAL_LAUNCH', value, error)}
                            name="ACTUAL_LAUNCH"
                            label={translate('providerDetail.actualLaunch')}
                            fullWidth
                            disabled={props.disableEdit || !props.permissionOnboardingEdit}
                            type="dateTime"
                        />
                    </GridItem>
                    <GridItem cols={3}>
                        <DateTimePicker
                            value={props.editData.PLANNED_OFFBOARDING}
                            error={props.editErrors.PLANNED_OFFBOARDING}
                            onChange={(value, error) => props.onChange('PLANNED_OFFBOARDING', value, error)}
                            name="PLANNED_OFFBOARDING"
                            label={translate('providerDetail.plannedOffboarding')}
                            fullWidth
                            disabled={props.disableEdit || !props.permissionOnboardingEdit}
                            type="date"
                        />
                    </GridItem>
                    <GridItem cols={3}>
                        <DateTimePicker
                            value={props.editData.ACTUAL_OFFBOARDING}
                            error={props.editErrors.ACTUAL_OFFBOARDING}
                            onChange={(value, error) => props.onChange('ACTUAL_OFFBOARDING', value, error)}
                            name="ACTUAL_OFFBOARDING"
                            label={translate('providerDetail.actualOffboarding')}
                            fullWidth
                            disabled={props.disableEdit || !props.permissionOnboardingEdit}
                            type="dateTime"
                        />
                    </GridItem>
                </Grid>
            </Box>

            <Grid>
                <GridItem cols={3}>
                    <Switch
                        value={!!props.editData.DEFAULT_PRINT_FILES}
                        onChange={(value) => props.onChange('DEFAULT_PRINT_FILES', value ? 1 : 0, false)}
                        disabled={props.disableEdit || !props.permissionOnboardingEdit}
                        label={translate('providerDetail.printFiles')}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Switch
                        value={!!props.editData.ENABLE_RETURN_LABELS}
                        onChange={(value) => {
                            const enableValue = value ? 1 : 0;
                            if (!value) {
                                // Když se vypne ENABLE_RETURN_LABELS, automaticky vypnout i PRINT_RETURN_LABEL
                                props.onChangeMultiple({
                                    ENABLE_RETURN_LABELS: enableValue,
                                    PRINT_RETURN_LABEL: 0
                                });
                            } else {
                                // Když se zapne ENABLE_RETURN_LABELS, nastavit jen tuto hodnotu
                                props.onChange('ENABLE_RETURN_LABELS', enableValue, false);
                            }
                        }}
                        disabled={props.disableEdit || !props.permissionOnboardingEdit}
                        label={translate('providerDetail.enableReturnLabels')}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Switch
                        value={!!props.editData.PRINT_RETURN_LABEL}
                        onChange={(value) => props.onChange('PRINT_RETURN_LABEL', value ? 1 : 0, false)}
                        disabled={props.disableEdit || !props.permissionOnboardingEdit || !props.editData.ENABLE_RETURN_LABELS}
                        label={translate('providerDetail.printReturnLabel')}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Switch
                        value={!!props.editData.ALLOW_ORDER_PRODUCT_DETAIL}
                        onChange={(value) => props.onChange('ALLOW_ORDER_PRODUCT_DETAIL', value ? 1 : 0, false)}
                        disabled={props.disableEdit || !props.permissionOnboardingEdit}
                        label={translate('providerDetail.orderProductDetail')}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Switch
                        value={!!props.editData.ALLOWED_ORDER_PRODUCTS_EDIT}
                        onChange={(value) => props.onChange('ALLOWED_ORDER_PRODUCTS_EDIT', value ? 1 : 0, false)}
                        disabled={props.disableEdit || !props.permissionOnboardingEdit}
                        label={translate('providerDetail.allowedOrderProductsEdit')}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Switch
                        value={!!props.editData.UNDER_SANCTION_ENABLED}
                        onChange={(value) => props.onChange('UNDER_SANCTION_ENABLED', value ? 1 : 0, false)}
                        disabled={props.disableEdit || !props.permissionOnboardingEdit}
                        label={translate('providerDetail.underSanctionEnabled')}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Switch
                        value={!!props.editData.BRANCH_ID_ENABLED}
                        onChange={(value) => props.onChange('BRANCH_ID_ENABLED', value ? 1 : 0, false)}
                        disabled={props.disableEdit || !props.permissionOnboardingEdit}
                        label={translate('providerDetail.branchIdEnabled')}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Switch
                        value={!!props.editData.ALLOW_DISABLE_SHIPPING_REORDER}
                        onChange={(value) => props.onChange('ALLOW_DISABLE_SHIPPING_REORDER', value ? 1 : 0, false)}
                        disabled={props.disableEdit || !props.permissionOnboardingEdit}
                        label={translate('providerDetail.allowDisableShippingReorder')}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Switch
                        value={!!props.editData.CUSTOMS_WAREHOUSE_ENABLED}
                        onChange={(value) => props.onChange('CUSTOMS_WAREHOUSE_ENABLED', value ? 1 : 0, false)}
                        disabled={props.disableEdit || !props.permissionOnboardingEdit}
                        label={translate('providerDetail.customsWarehouseEnabled')}
                    />
                </GridItem>
                <GridItem cols={3}>
                    <Box data-testid="provider-dcix-export-personal-data-switch">
                        <Switch
                            value={props.editData.DCIX_EXPORT_PERSONAL_DATA !== 0}
                            onChange={(value) => props.onChange('DCIX_EXPORT_PERSONAL_DATA', value ? 1 : 0, false)}
                            disabled={props.disableEdit || !props.permissionOnboardingEdit}
                            label={translate('providerDetail.dcixExportPersonalData')}
                        />
                    </Box>
                </GridItem>
            </Grid>
        </>
    );
};

export default It;
